import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Mail } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";
import { DirectoryEntry } from "@/data/directoryTypes";

const emailSchema = z.string().trim().email("Please enter a valid email address").max(255);

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: DirectoryEntry[];
}

const ExportDialog = ({ open, onOpenChange, entries }: ExportDialogProps) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const handleExport = () => {
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }
    setError("");

    const headers = ["Name", "Type", "Scope", "Primary Job", "CIP Areas", "Why It Matters", "Ideal Buyer", "Partner Angle", "Links", "Confidence", "Category"];
    const escapeCSV = (val: string) => `"${val.replace(/"/g, '""')}"`;
    const rows = entries.map((e) => [
      e.name, e.type, e.scope, e.primaryJob, e.cipAreas.join("; "),
      e.whyItMatters, e.idealBuyer, e.partnerAngle, e.links, e.confidence, e.category,
    ].map(escapeCSV).join(","));

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nerc-cip-directory.csv";
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: "Download started", description: `CSV with ${entries.length} entries is downloading.` });
    onOpenChange(false);
    setEmail("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download size={18} className="text-primary" />
            Export Directory as CSV
          </DialogTitle>
          <DialogDescription>
            Enter your email to download {entries.length} directory entries as a CSV file.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="export-email">Email address</Label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="export-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                className="pl-10"
                onKeyDown={(e) => e.key === "Enter" && handleExport()}
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
          <Button onClick={handleExport} className="w-full">
            <Download size={16} className="mr-2" />
            Download CSV
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            We respect your privacy. Your email is not stored.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
